/**
 * @fileoverview Waitlist Call-to-Action button component
 * @active CURRENT ACTIVE IMPLEMENTATION
 * 
 * Provides an elegant, animated CTA for waitlist enrollment with gradient styling
 * to match the premium feel of the WaitlistOverlay component.
 * 
 * @see {@link src/components/ui/WaitlistOverlay.tsx} - Modal overlay for waitlist signup
 * @see {@link src/components/ui/header/SubHeader.tsx} - Parent component where this is used
 */

"use client";

import { useState, useEffect } from "react";
import { Box, Flex, Text, useDisclosure, useMediaQuery } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import WaitlistOverlay from "@/components/ui/WaitlistOverlay";
import { useBreakPoints } from "@/hooks/useBreakPoints";

// Subtle shine effect animation
const shine = keyframes`
  0% { background-position: -100px; }
  60% { background-position: 250px; }
  100% { background-position: 250px; }
`;

// Pulse animation for the button
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

interface WaitlistCTAProps {
  /** Whether to use mobile styling */
  isMobile?: boolean;
}

/**
 * Premium, visually appealing waitlist CTA with subtle animations
 * 
 * @component
 * @param {WaitlistCTAProps} props - Component props
 * @returns {JSX.Element} Animated waitlist CTA buttons
 */
const WaitlistCTANavButton = ({ isMobile = false }: WaitlistCTAProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isTablet } = useBreakPoints();
  const [isCompactView] = useMediaQuery("(max-width: 1100px)");
  
  // Display for mobile navigation drawer
  if (isMobile) {
    return (
      <>
        <Flex 
          direction="column" 
          align="center" 
          w="full" 
          py={2}
        >
          <Box
            onClick={onOpen}
            cursor="pointer"
            bgGradient="linear(135deg, #9066E3, #E366B5, #66C1E3)"
            borderRadius="full"
            px={5}
            py={2}
            color="white"
            fontWeight="600"
            fontSize="14px"
            boxShadow="0 2px 10px rgba(144, 102, 227, 0.3)"
            position="relative"
            overflow="hidden"
            transition="all 0.2s ease"
            _hover={{
              boxShadow: "0 4px 15px rgba(144, 102, 227, 0.5)",
              transform: "translateY(-1px)"
            }}
            sx={{
              animation: `${pulse} 3s infinite ease-in-out`
            }}
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
              backgroundSize: '200px 100%',
              animation: `${shine} 3s infinite linear`,
            }}
          >
            <Flex direction="column" align="center">
              <Text>Early Access Waitlist ✨</Text>
              <Text fontSize="10px" fontWeight="400" opacity={0.9} mt="2px">
                Coming May 2025
              </Text>
            </Flex>
          </Box>
        </Flex>
        
        <WaitlistOverlay 
          isOpen={isOpen}
          onClose={onClose}
        />
      </>
    );
  }

  // Handle compact layout for narrower screens
  if (isCompactView) {
    return (
      <>
        <Box
          onClick={onOpen}
          cursor="pointer"
          bgGradient="linear(to-r, #9066E3, #E366B5)"
          px={{ base: 3, md: 3.5 }}
          py="6px"
          h="30px"
          borderRadius="md"
          color="white"
          fontWeight="600"
          fontSize="13px"
          boxShadow="0 2px 8px rgba(144, 102, 227, 0.15)"
          position="relative"
          overflow="hidden"
          display="flex"
          alignItems="center"
          justifyContent="center"
          transition="all 0.2s ease"
          minW="auto"
          maxW="100%"
          whiteSpace="nowrap"
          _hover={{
            boxShadow: "0 4px 12px rgba(144, 102, 227, 0.3)",
            transform: "translateY(-1px)"
          }}
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
            backgroundSize: '200px 100%',
            animation: `${shine} 3s infinite linear`,
          }}
        >
          <Flex align="center" gap={1}>
            <Box 
              as="span" 
              fontSize="14px"
              position="relative"
              top="1px"
            >
              ✨
            </Box>
            <Text lineHeight="1">Early Access</Text>
          </Flex>
        </Box>
        
        <WaitlistOverlay 
          isOpen={isOpen}
          onClose={onClose}
        />
      </>
    );
  }

  // Default display for desktop header
  return (
    <>
      <Flex 
        align="center" 
        gap={1.5} 
        onClick={onOpen}
        cursor="pointer"
        role="group"
        position="relative"
        maxW={{ base: "100%", md: "auto" }}
        flexShrink={0}
      >
        {/* Date element to the left */}
        <Flex
          align="center"
          justify="center"
          h="30px"
          px={{ base: 2, md: 3 }}
          borderRadius="md"
          bg="rgba(144, 102, 227, 0.05)"
          border="1px solid"
          borderColor="rgba(144, 102, 227, 0.2)"
          transition="all 0.2s ease"
          flexShrink={0}
          _groupHover={{
            borderColor: "rgba(144, 102, 227, 0.4)",
            bg: "rgba(144, 102, 227, 0.08)"
          }}
        >
          <Text
            fontSize={{ base: "10px", md: "11px" }}
            fontWeight="600"
            letterSpacing="0.3px"
            color="#9066E3"
            whiteSpace="nowrap"
          >
            Coming May 2025
          </Text>
        </Flex>

        {/* Main CTA button to the right */}
        <Box
          bgGradient="linear(to-r, #9066E3, #E366B5)"
          px={{ base: 3, md: 4 }}
          py="6px"
          h="30px"
          borderRadius="md"
          color="white"
          fontWeight="600"
          fontSize="13px"
          boxShadow="0 2px 8px rgba(144, 102, 227, 0.15)"
          position="relative"
          overflow="hidden"
          display="flex"
          alignItems="center"
          justifyContent="center"
          transition="all 0.2s ease"
          minW="auto"
          maxW="100%"
          whiteSpace="nowrap"
          flexShrink={0}
          _groupHover={{
            boxShadow: "0 4px 12px rgba(144, 102, 227, 0.3)",
            transform: "translateY(-1px)"
          }}
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
            backgroundSize: '200px 100%',
            animation: `${shine} 3s infinite linear`,
          }}
        >
          <Flex align="center" gap={1}>
            <Box 
              as="span" 
              fontSize="14px"
              position="relative"
              top="1px"
            >
              ✨
            </Box>
            <Text lineHeight="1">Get Early Access</Text>
          </Flex>
        </Box>
      </Flex>
      
      <WaitlistOverlay 
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
};

export default WaitlistCTANavButton; 